/* eslint-disable @typescript-eslint/prefer-for-of */
/* eslint-disable prefer-destructuring,no-param-reassign */
import { toJS, transaction } from 'mobx';
import { mobxSet } from '@src/slide/infrastructure/module/independent/assert/get-set';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import set from 'lodash/set';
import has from 'lodash/has';
import _get from 'lodash/get';
import TextAdjustManager from '@src/slide/domain/ui-model/element-modeles/property/text/text-adjust';
import SnapLineManager from '@framework/editor/src/page/index/components/canvas/snapline-manager';
import { isShapeAutoFit } from '@src/slide/domain/ui-model/element-modeles/property/text/text-adjust/utils';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { getControlValues } from '@editor/src/page/index/components/canvas/element-types/shape-element/utils';
import { transaction as sdkTransaction } from '@src/slide/domain/slide-app';
import { getLogicPropertyKeyMap } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-direction';
import type { ISnapLineService } from '@src/slide/application/service/snap-line-service/interface/snap-line-service.interface';
import type { IGuidesManager } from '@src/slide/application/service/guides-manager/index.interface';
import type { IOffsetFrame } from '@editor/src/page/index/components/canvas/snapline-logic';
import type { IResizableSize } from '@src/framework/editor/src/page/index/components/canvas/resizable.interface';
import type { IAbsorbManager } from '@src/slide/application/service/absorb-manager/absorb-manager.interface';
import type { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import type { IMultiRect } from './edit-process.interface';
import type { BorderSketchManager } from '@src/slide/application/service/border-sketch-manager';
import type { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import type { ISmartArtModel } from '@src/slide/domain/ui-model/element-modeles/Interface/smart-art-model.interface';
import type { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import type { IConnectorModel } from '@src/slide/domain/ui-model/element-modeles/Interface/connector-model.interface';
import type { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import type EditingStateManager from '@src/slide/application/service/editing-state-manager';
import { DeltaOf, ITableData } from '@tencent/slide-sdk-model';

type IPropsForUiModel = {
    curItem: any, toRemoveUIModels: any[], toRemovePropsArr: any[],
    mode: string, needAdjustText?: boolean
};

export default class EditProcess {
    /**
     * 当前操作的元素model
     */
    private readonly elementModel: IElementBaseModel | undefined;

    private editingStateManagerRef: EditingStateManager;
    private selectionService: ISelectionService;
    private borderSketchManager?: BorderSketchManager;
    private guidesManager?: IGuidesManager;
    private absorbManager?: IAbsorbManager;
    private snapLineService?: ISnapLineService;

    public constructor(
        editingStateManager: EditingStateManager,
        elementModel?: IElementBaseModel,
        borderSketchManager?: BorderSketchManager,
    ) {
        this.editingStateManagerRef = editingStateManager;
        this.elementModel = elementModel;
        this.borderSketchManager = borderSketchManager;
    }

    public setSelectionService(selectionService: ISelectionService): void {
        if (this.selectionService) return;
        this.selectionService = selectionService;
    }

    public setGuidesManagerDelegate(guidesManager?: IGuidesManager): void {
        this.guidesManager = guidesManager;
    }

    public setAbsorbManagerDelegate(absorbManager?: IAbsorbManager): void {
        this.absorbManager = absorbManager;
    }

    public setSnapLineServiceDelegate(snapLineService?: ISnapLineService): void {
        this.snapLineService = snapLineService;
    }

    /**
     * 更新选中元素操作中的Frame
     * @param offsetFrame
     * @param sizeMap? 该对象中储存了每个选中元素的offsetFrame
     */
    public updateOperationFrame(offsetFrame, sizeMap?, mode?: string): void {
        transaction(() => {
            const selectedUIModelArr = this.selectionService
                .selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE);
            for (let i = 0; i < selectedUIModelArr.length; i++) {
                const selectedUIModel = selectedUIModelArr[i];
                if (sizeMap?.size > 1) {
                    offsetFrame = sizeMap.get(selectedUIModel.key)?.offsetFrame;
                }
                if (offsetFrame === null) {
                    mobxSet(selectedUIModel, 'operationFrame', null);
                    mobxSet(selectedUIModel, 'operatingGraphicInfo', null);
                } else {
                    this.updateForConnector(selectedUIModel, offsetFrame, mode);
                }
                this.updateRelatedOperationFrame(selectedUIModel, offsetFrame, sizeMap);
            }
        });
    }

    public runConstraints = (
        e, originalSize: IResizableSize, nextSize: IResizableSize, mode: string,
        multiRect?: IMultiRect | null, sizeMap?,
    ) => {
        let needReturn = false;
        let offsetFrame;
        transaction(() => {
            this.calFrameForSizeMap(e, sizeMap, mode, multiRect);
            offsetFrame = this.calOffsetFrame(originalSize, nextSize);
            const originOffsetFrame = Object.assign({}, offsetFrame);
            if (this.elementModel) {
                // 参考线 和 网格线 还没有处理 rotate，会导致问题。
                // 后续用 snapLineService 抽的公共能力处理
                const hasNoRotate = this.elementModel.props.style.rotate === 0;
                if (hasNoRotate) {
                    // 参考线吸附 todo: 后续放到公共manager里
                    if (this.guidesManager?.displayGuides) {
                        this.guidesManager.handleDragAdsorbGuides({
                            offsetFrame,
                            originalSize,
                            mode,
                            model: this.elementModel,
                            multiRect,
                            sizeMap,
                        });
                    }

                    // 网格线吸附 todo: 处理下吸附之间的冲突
                    this.absorbManager?.handleGridAbsorbElement({
                        offsetFrame,
                        originalSize,
                        mode,
                        model: this.elementModel,
                        multiRect,
                        sizeMap,
                    });
                }

                // 对齐线吸附
                this.handleSnapLineAdsorb({
                    e,
                    originalSize,
                    nextSize,
                    offsetFrame,
                    mode,
                    multiRect,
                    elementModel: this.elementModel,
                    sizeMap,
                });

                if (_get(this.elementModel, 'parent.type') === UIModelType.GROUP || _get(this.elementModel, 'parent.type') === UIModelType.SMART_ART) {
                    needReturn = true;
                }
            }
            !needReturn && this.updateOperationFrame(offsetFrame, sizeMap, mode);
        });
        if (needReturn) return offsetFrame;
    };

    public persistSize = (mode: string, needAdjustText?: boolean, selectedArr?: IBaseModel[]): void => {
        const { selectedElements } = this.selectionService;
        const selectedUiModels = selectedArr ?? selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE);
        // toRemoveUIModels, toRemovePropsArr
        const toRemoveUM = [];
        const toRemovePA = [];
        sdkTransaction(() => {
            const propsArr = selectedUiModels.map((curItem) => {
                const eleProps = this.propsForUiModel({
                    curItem, toRemoveUIModels: toRemoveUM, toRemovePropsArr: toRemovePA, mode, needAdjustText,
                });
                if (mode !== MODES.MOVE) {
                    this.borderSketchManager?.updatePathListWhenSizeChange(curItem);
                }
                return eleProps;
            });
            const uiModels = selectedUiModels.map(uiModel => uiModel);
            this.handleRelatedInfo(selectedUiModels, uiModels, propsArr);
            this.commitChange(toRemoveUM, uiModels, toRemovePA, propsArr);
        });
    };

    public runConstraintsForGroup = (
        e, originalSize: IResizableSize, nextSize: IResizableSize, mode, srcRect,
        offsetFrame, sizeMap?,
    ): void => {
        if (!offsetFrame) {
            offsetFrame = this.calOffsetFrame(originalSize, nextSize);
        }
        this.updateOperationFrameForGroup(offsetFrame, sizeMap);
    };

    public updateOperationFrameForGroup = (offsetFrame, sizeMap?): void => {
        transaction(() => {
            const selectedUIModelArr = this.selectionService.selectedElements
                .selectedArrayForType(SelectedEleMgrType.GROUP);
            for (let i = 0; i < selectedUIModelArr.length; i++) {
                const selectedUIModel = selectedUIModelArr[i];
                if (sizeMap?.size > 1) {
                    offsetFrame = sizeMap.get(selectedUIModel.key)?.offsetFrame;
                }
                if (offsetFrame === null) {
                    mobxSet(selectedUIModel, 'operationFrame', null);
                } else {
                    this.updateSelectedUIModelFrame(selectedUIModel, offsetFrame);
                }
                this.updateRelatedInfoForConnector(selectedUIModel);
                for (let j = 0; j < selectedUIModel.relatedUIModelRefs.length; j++) {
                    const connectorModel = selectedUIModel.relatedUIModelRefs[j];
                    const updateSytle = connectorModel.connectorCaculater.getUpdateStyle(offsetFrame, sizeMap);
                    connectorModel.updateModelStyle(updateSytle);
                }
            }
        });
    };

    /**
     * @param notCommit 不提交数据更新。默认为 false
     */
    public persistSizeForGroup = (
        groupModel?: IGroupModel | null, notCommit = false,
        selectedArr?: IElementBaseModel[],
    ): any => {
        const selectedUiModels = selectedArr ?? this.selectionService
            .selectedElements
            .selectedArrayForType(SelectedEleMgrType.GROUP);

        const updateModels = [];
        const propsArr = [];
        const toRemoveUIModels = [];
        const toRemovePropsArr = [];

        // 先调整group相关数据， element如果发生旋转或者翻转后，会依赖于group上的变更追加宽高变更的changeProArr
        const arr = groupModel?.getFrame().getAdjustedElementsAndProps() ?? [];
        arr.forEach((item) => {
            if (item.uiModel.type === UIModelType.GROUP) {
                return;
            }
            // @ts-ignore
            propsArr.push(item.props);
            // @ts-ignore
            updateModels.push(item.uiModel);
        });

        selectedUiModels.forEach((item) => {
            this.calculateSelectedModelForGroup(item, toRemoveUIModels, toRemovePropsArr, updateModels, propsArr);
        });
        this.getRelatedUIModelsChangesForGroup(selectedUiModels, updateModels, propsArr);

        if (notCommit) {
            return {
                toRemoveUIModels,
                updateModels,
                toRemovePropsArr,
                propsArr,
                selectedUiModels,
                groupModel,
            };
        }
        this.commitChangeForGroup(
            toRemoveUIModels,
            updateModels,
            toRemovePropsArr,
            propsArr,
            selectedUiModels,
            groupModel,
        );
    };

    // [SmartArt] 拖拽移动 SmartArt 中的子元素逻辑（to do--暂时保留，后续处理子元素需要）
    public runConstraintsForSmartArt = (
        e, originalSize: IResizableSize, nextSize: IResizableSize, mode, srcRect,
        offsetFrame, sizeMap?,
    ): void => {
        if (!offsetFrame) {
            offsetFrame = this.calOffsetFrame(originalSize, nextSize);
        }
        this.updateOperationFrameForSmartArt(offsetFrame, sizeMap);
    };

    // [SmartArt] 更新 SmartArt 中子元素的 frame 信息
    public updateOperationFrameForSmartArt = (offsetFrame, sizeMap?): void => {
        transaction(() => {
            // console.log('log-updateOperationFrameForSmartArt', offsetFrame, sizeMap);
            const selectedUIModelArr = this.selectionService.selectedElements
                .selectedArrayForType(SelectedEleMgrType.SMART_ART);
            for (let i = 0; i < selectedUIModelArr.length; i++) {
                const selectedUIModel = selectedUIModelArr[i];
                console.log('log-updateOperationFrameForSmartArt', selectedUIModel);
                if (sizeMap?.size > 1) {
                    offsetFrame = sizeMap.get(selectedUIModel.key)?.offsetFrame;
                }
                // console.log('log-selectedElements-offsetFrame', offsetFrame);
                if (offsetFrame === null) {
                    mobxSet(selectedUIModel, 'operationFrame', null);
                } else {
                    this.updateSelectedUIModelFrame(selectedUIModel, offsetFrame);
                }
                // this.updateRelatedInfoForConnector(selectedUIModel);
                // for (let j = 0; j < selectedUIModel.relatedUIModelRefs.length; j++) {
                //     const connectorModel = selectedUIModel.relatedUIModelRefs[j];
                //     const updateSytle = connectorModel.connectorCaculater.getUpdateStyle(offsetFrame, sizeMap);
                //     connectorModel.updateModelStyle(updateSytle);
                // }
            }
        });
    };

    /**
     * @param notCommit 不提交数据更新。默认为 false
     */
    public persistSizeForSmartArt = (
        smartArtModel?: ISmartArtModel | null,
        notCommit = false, selectedArr?: IElementBaseModel[],
    ): any => {
        const selectedUiModels = selectedArr ?? this.selectionService.selectedElements
            .selectedArrayForType(SelectedEleMgrType.SMART_ART);

        const updateModels = [];
        const propsArr = [];
        const toRemoveUIModels = [];
        const toRemovePropsArr = [];

        // 先调整 smartArt 相关数据， element 如果发生旋转或者翻转后，会依赖于 smartArt 上的变更追加宽高变更的 changeProArr
        const arr = smartArtModel?.getFrame().getAdjustedElementsAndProps() ?? [];
        arr.forEach((item) => {
            this.borderSketchManager?.updatePathListWhenSizeChange(item.uiModel, item.props.style);
            if (item.uiModel.type === UIModelType.SMART_ART) {
                return;
            }
            // @ts-ignore
            propsArr.push(item.props);
            // @ts-ignore
            updateModels.push(item.uiModel);
        });

        selectedUiModels.forEach((item) => {
            this.calculateSelectedModelForGroup(item, toRemoveUIModels, toRemovePropsArr, updateModels, propsArr);
        });
        this.getRelatedUIModelsChangesForGroup(selectedUiModels, updateModels, propsArr);

        if (notCommit) {
            return {
                toRemoveUIModels,
                updateModels,
                toRemovePropsArr,
                propsArr,
                selectedUiModels,
                smartArtModel,
            };
        }
        this.commitChangeForSmartArt(
            toRemoveUIModels,
            updateModels,
            toRemovePropsArr,
            propsArr,
            selectedUiModels,
            smartArtModel,
        );
    };

    private commitChangeForSmartArt(
        toRemoveUIModels: IElementBaseModel[],
        updateModels: IElementBaseModel[],
        toRemovePropsArr: any[],
        propsArr: any[],
        selectedUiModels: IElementBaseModel[],
        smartArtModel?: ISmartArtModel | null,
    ) {
        this.commitChange(toRemoveUIModels, updateModels, toRemovePropsArr, propsArr);
        if (selectedUiModels?.length > 0) {
            smartArtModel?.getFrame().clearTempInfosWithElement(selectedUiModels[0]);
        }
    }

    private updateRelatedOperationFrame(model, offsetFrame, sizeMap, changedRelatedModelList?) {
        if (!changedRelatedModelList) {
            changedRelatedModelList = [];
        }
        for (let j = 0; j < model.relatedUIModelRefs.length; j++) {
            const relatedUIModel = model.relatedUIModelRefs[j];
            if (!changedRelatedModelList.find(item => item.key === relatedUIModel.key)) {
                const updateSytle = relatedUIModel.connectorCaculater.getUpdateStyle(offsetFrame, sizeMap);
                relatedUIModel.updateModelStyle(updateSytle);
                changedRelatedModelList.push(relatedUIModel);
            }
        }
        if (model.type === UIModelType.GROUP) {
            model.children.forEach((childModel) => {
                this.updateRelatedOperationFrame(childModel, offsetFrame, sizeMap, changedRelatedModelList);
            });
        }
    }

    private calculateSelectedModelForGroup(
        item, toRemoveUIModels: any[],
        toRemovePropsArr: any[], updateModels: any[], propsArr: any[],
    ) {
        if (item.type === UIModelType.CONNECTOR) {
            const toRemoveProps = item.toRemoveConnectorInfoProps();
            if (toRemoveProps) {
                // @ts-ignore
                toRemoveUIModels.push(item);
                // @ts-ignore
                toRemovePropsArr.push(toRemoveProps);
            }
        }

        const exist = this.findIfRelatedModelNeedUpdate(updateModels, item, propsArr);
        if (!exist) {
            // @ts-ignore
            updateModels.push(item);
            propsArr.push(this.frameInfoOfModel(item));
        }
        if (TextAdjustManager.getInstance().enableAdjustText()) {
            this.updateConnectorInfo(item, updateModels);
        }
    }

    // 更新组合中元素连接线位置
    private updateConnectorInfo(item, updateModels: any[]) {
        if (isShapeAutoFit(item)) {
            updateModels?.forEach((uiModel) => {
                if (uiModel === item) {
                    const offsetFrame = this.getOffsetFrame(uiModel.props.style, item.operationFrame);
                    item?.relatedUIModelRefs?.forEach((relatedUIModel) => {
                        const updateSytle = relatedUIModel.connectorCaculater.getUpdateStyle(offsetFrame);
                        relatedUIModel.updateModelStyle(updateSytle);
                    });
                }
            });
        }
    }

    private getOffsetFrame(newFrame, curFrame) {
        return {
            height: newFrame.height - curFrame?.height,
            width: newFrame.width - curFrame?.width,
            left: newFrame.left - curFrame?.left,
            top: newFrame.top - curFrame?.top,
            flipH: newFrame.flipH,
            flipV: newFrame.flipV,
            rotate: newFrame.rotate,
        };
    }

    private findIfRelatedModelNeedUpdate(updateModels: any[], item, propsArr: any[]) {
        let exist = false;
        for (let i = 0; i < updateModels.length; i++) {
            if (updateModels[i] === item) {
                exist = true;
                propsArr[i].style.rotate = item.props.style.rotate;
                propsArr[i].style.flipH = has(item, 'operationFrame.flipH') ? item.operationFrame.flipH : item.props.style.flipH;
                propsArr[i].style.flipV = has(item, 'operationFrame.flipV') ? item.operationFrame.flipV : item.props.style.flipV;
                if (item.props.style.controlValues) {
                    propsArr[i].style.controlValues = toJS(item.props.style.controlValues);
                }
                if (item.props.style.bgLine) {
                    propsArr[i].style.bgLine = item.props.style.bgLine;
                }
                break;
            }
        }
        return exist;
    }

    private frameInfoOfModel(model) {
        const { left, top, width, height, flipH, flipV } = model.getFrame().getFrameInfo(true);
        return {
            style: {
                left, top, width, height, flipH, flipV,
                rotate: model.props.style.rotate,
            },
        };
    }

    private commitChangeForGroup(
        toRemoveUIModels: IElementBaseModel[],
        updateModels: IElementBaseModel[],
        toRemovePropsArr: any[],
        propsArr: any[],
        selectedUiModels: IElementBaseModel[],
        groupModel?: IGroupModel | null,
    ) {
        sdkTransaction(() => {
            updateModels.forEach((model) => {
                this.borderSketchManager?.updatePathListWhenSizeChange(model);
            });
            this.commitChange(toRemoveUIModels, updateModels, toRemovePropsArr, propsArr);
        });
        if (selectedUiModels?.length > 0) {
            groupModel?.getFrame().clearTempInfosWithElement(selectedUiModels[0]);
        }
    }

    private getRelatedUIModelsChangesForGroup(selectedUiModels, updateModels, propsArr) {
        for (let i = 0; i < selectedUiModels.length; i++) {
            const selectedUIModel = selectedUiModels[i];
            for (let j = 0; j < selectedUIModel.relatedUIModelRefs.length; j++) {
                this.getConnectorUpdateInfo(selectedUIModel, j, updateModels, propsArr);
            }
        }
        return {
            updateModels,
            propsArr,
        };
    }

    private getConnectorUpdateInfo(selectedUIModel, j: number, updateModels, propsArr) {
        const relatedUIModel = selectedUIModel.relatedUIModelRefs[j];
        if (relatedUIModel.type === UIModelType.CONNECTOR) {
            const exist = this.findIfRelatedModelNeedUpdate(updateModels, relatedUIModel, propsArr);
            if (!exist) {
                updateModels.push(relatedUIModel);
                propsArr.push({
                    style: {
                        ...relatedUIModel.operationFrame,
                        // @ts-ignore
                        bgLine: relatedUIModel.props.style.bgLine || relatedUIModel.bgLine,
                        // @ts-ignore
                        controlValues: toJS(relatedUIModel.props.style.controlValues || relatedUIModel.controlValues),
                    },
                });
            }
        }
    }

    private updateSelectedUIModelFrame(selectedUIModel, offsetFrame, mode?: string) {
        let newWidth = selectedUIModel.props.style.width + offsetFrame.width;
        let newHeight = selectedUIModel.props.style.height + offsetFrame.height;

        // 表格不允许反向缩放
        if (selectedUIModel.type === UIModelType.TABLE) {
            newWidth = offsetFrame.flipH ? 0 : newWidth;
            newHeight = offsetFrame.flipV ? 0 : newHeight;
        }

        mobxSet(selectedUIModel, 'operationFrame', {
            left: selectedUIModel.props.style.left + offsetFrame.left,
            top: selectedUIModel.props.style.top + offsetFrame.top,
            width: newWidth,
            height: newHeight,
        });
        if (has(offsetFrame, 'flipH')) {
            mobxSet(selectedUIModel, 'operationFrame.flipH', offsetFrame.flipH);
        }
        if (has(offsetFrame, 'flipV')) {
            mobxSet(selectedUIModel, 'operationFrame.flipV', offsetFrame.flipV);
        }
        if (mode !== MODES.MOVE) {
            const pathList = this.borderSketchManager?.getSketchPathList(selectedUIModel, {
                width: selectedUIModel.props.style.width + offsetFrame.width,
                height: selectedUIModel.props.style.height + offsetFrame.height,
            });
            const controlValues = getControlValues(selectedUIModel, selectedUIModel.props);
            if ((pathList as Array<any>)?.length > 0) {
                mobxSet(selectedUIModel, 'operatingGraphicInfo', {
                    controlValues,
                    pathList,
                });
            }
        }
    }

    private updateForConnector(selectedUIModel, offsetFrame, mode?: string) {
        let needUpdate = true;
        if (selectedUIModel.type === UIModelType.CONNECTOR) {
            const startModel = selectedUIModel.startUIModel;
            const endModel = selectedUIModel.endUIModel;
            needUpdate = this.checkIfNeedUpdateForConnector(startModel, selectedUIModel, endModel, needUpdate);
        }
        if (needUpdate) {
            this.updateSelectedUIModelFrame(selectedUIModel, offsetFrame, mode);
        }
    }

    private handleRelatedInfo(selectedUiModels, uiModels, propsArr) {
        for (let i = 0; i < selectedUiModels.length; i++) {
            const selectedUIModel = selectedUiModels[i];
            for (let j = 0; j < selectedUIModel.relatedUIModelRefs.length; j++) {
                const relatedUIModel = selectedUIModel.relatedUIModelRefs[j];
                if (relatedUIModel.type === UIModelType.CONNECTOR
                    && !uiModels.find(item => item.key === relatedUIModel.key)) {
                    uiModels.push(relatedUIModel);
                    propsArr.push({
                        style: {
                            ...relatedUIModel.operationFrame,
                            bgLine: relatedUIModel.props.style.bgLine,
                            controlValues: toJS(relatedUIModel.props.style.controlValues),
                        },
                    });
                }
            }
            // 组合元素需要递归处理子元素的关联关系
            if (selectedUIModel.type === UIModelType.GROUP) {
                this.handleRelatedInfo(selectedUIModel.children, uiModels, propsArr);
            }
        }
    }

    private commitChange(
        toRemoveUIModels: IElementBaseModel[], uiModels: IElementBaseModel[],
        toRemovePropsArr: any[], propsArr,
    ) {
        if (toRemoveUIModels.length > 0) {
            uiModels.forEach(() => toRemovePropsArr.unshift(undefined)); // 补齐前面的空对象
            toRemoveUIModels.forEach(() => propsArr.push(null));
            this.selectionService.currentSlide?.updateAndRemovePropsForElements(
                propsArr,
                toRemovePropsArr,
                uiModels.concat(toRemoveUIModels),
            );
        } else {
            this.selectionService.currentSlide?.updateElementsProps(propsArr, uiModels);
        }
    }

    private propsForUiModel(props: IPropsForUiModel) {
        const { curItem, toRemoveUIModels, toRemovePropsArr, mode, needAdjustText = true } = props;
        if (curItem.type === UIModelType.CONNECTOR) {
            const toRemoveProps = curItem.toRemoveConnectorInfoProps();
            if (toRemoveProps) {
                toRemoveUIModels.push(curItem);
                toRemovePropsArr.push(toRemoveProps);
            }
        }
        if (curItem.type === UIModelType.TABLE && curItem.operationFrame && (curItem.props.style.width
            !== curItem.operationFrame.width || curItem.props.style.height !== curItem.operationFrame.height)) {
            return curItem.resizeTable(curItem.operationFrame, mode);
        }
        if (this.isNeedToAdjustText(curItem, mode, needAdjustText)) {
            // 形状设置了跟随文本大小调整属性，缩放结束时对frame信息进行调整
            return this.frameInfoAfterAdjust(curItem, mode);
        }
        return this.frameInfoOfModel(curItem);
    }

    private frameInfoAfterAdjust(curItem, mode) {
        const { blockSize, inlineSize } = getLogicPropertyKeyMap(curItem?.textBodyStyle?.wrapperStyle?.textDirection);
        const frame = this.frameInfoOfModel(curItem);
        if (['flipH', 'flipV', 'rotate', inlineSize].every(key => frame.style[key] === curItem.props.style[key])) {
            // 由于形状是跟随文字调整大小的，如果这四个值都没变化，根据 office 的表现，形状的 left top blockSize 也都不会变化，所以不用再重新计算尺寸了
            return {
                style: {
                    ...frame.style,
                    [blockSize]: curItem.props.style[blockSize],
                    left: curItem.props.style.left,
                    top: curItem.props.style.top,
                },
            };
        }
        const newFrame = TextAdjustManager.getInstance().adjustShape(curItem, frame, mode);
        const offsetFrame = this.getOffsetFrame(newFrame.style, curItem.props.style);

        this.updateForConnector(curItem, offsetFrame);
        curItem?.relatedUIModelRefs?.forEach((relatedUIModel) => {
            const updateSytle = relatedUIModel.connectorCaculater.getUpdateStyle(offsetFrame);
            relatedUIModel.updateModelStyle(updateSytle);
        });

        return newFrame;
    }

    private updateRelatedInfoForConnector(selectedUIModel) {
        if (selectedUIModel.type === UIModelType.CONNECTOR) {
            // 如果连接的图形也没有被选中，需要被置空
            (selectedUIModel as IConnectorModel)
                .resetStartAndEndForMove(this.editingStateManagerRef.isDragging);
        }
    }

    private checkIfNeedUpdateForConnector(
        startModel: IElementBaseModel, selectedUIModel: IConnectorModel,
        endModel: IElementBaseModel, needUpdate: boolean,
    ) {
        if (this.editingStateManagerRef.isDragging) {
            selectedUIModel.resetStartAndEndForMove(true);
            // @ts-ignore
        } else if ((startModel?.selected) || (endModel?.selected)) {
            needUpdate = false;
        }
        return needUpdate;
    }

    /**
     * 计算每个选中元素的offsetFrame
     */
    private calFrameForSizeMap(e, sizeMap, mode: string, multiRect?: IMultiRect | null) {
        if (sizeMap?.size > 1) {
            sizeMap.forEach((value) => {
                value.offsetFrame = this.calOffsetFrame(value.originalSize, value.nextSize);
            });
        }
    }

    /**
     * 通过传入的originalSize和nextSize计算元素的frame
     * @param originalSize 初始的size
     * @param nextSize 变更后的size
     * @returns 计算后的offsetFrame
     */
    private calOffsetFrame(originalSize: IResizableSize, nextSize: IResizableSize) {
        if (!originalSize || !nextSize) return {};
        const offsetFrame = {
            left: (nextSize.left - originalSize.left),
            top: (nextSize.top - originalSize.top),
            width: (nextSize.width - originalSize.width),
            height: (nextSize.height - originalSize.height),
        };
        if (has(nextSize, 'flipH')) {
            set(offsetFrame, 'flipH', nextSize.flipH);
        }
        if (has(nextSize, 'flipV')) {
            set(offsetFrame, 'flipV', nextSize.flipV);
        }
        return offsetFrame;
    }

    private isNeedToAdjustText = (curItem, mode: string, needAdjust): boolean => TextAdjustManager
        .getInstance().enableAdjustText()
    && isShapeAutoFit(curItem)
    && mode !== MODES.MOVE
    && needAdjust;

    private checkOffsetIsChanged = (oldOffset: IOffsetFrame, newOffset: IOffsetFrame) => {
        let isChange = false;
        Object.keys(oldOffset).forEach((key) => {
            if (oldOffset[key] !== newOffset[key]) isChange = true;
        });
        return isChange;
    };

    private handleSnapLineAdsorb = (param: {
        e: any,
        originalSize: any,
        nextSize: any,
        offsetFrame: any,
        mode: any,
        multiRect: any,
        elementModel: IElementBaseModel
        sizeMap: any,
    }) => {
        this.snapLineService?.handleNotifyEvent(param);
    };
}
